import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Users, User } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";

export default function Announcements({ employees = [] }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all"); // all | individual
  const [employeeId, setEmployeeId] = useState("");
  const [sending, setSending] = useState(false);
  const [list, setList] = useState([]);

  const withEmail = employees.filter((e) => e.email);
  const load = useCallback(() => {
    api.get("/announcements").then(({ data }) => setList(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and a message"); return; }
    if (audience === "individual" && !employeeId) { toast.error("Choose an employee"); return; }
    setSending(true);
    try {
      const payload = { subject: subject.trim(), body: body.trim(), employee_id: audience === "individual" ? employeeId : null };
      const { data } = await api.post("/announcements", payload);
      if (data.emailed > 0) toast.success(`Announcement sent to ${data.emailed} employee${data.emailed !== 1 ? "s" : ""}`);
      else toast.warning("Saved, but no recipients had an email on file");
      setSubject(""); setBody(""); setEmployeeId(""); setAudience("all");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Announcements</h1>
        <p className="text-slate-400 text-sm mt-1">Email your whole team or a single employee</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5" data-testid="announcement-compose">
          <div className="flex items-center gap-2 mb-4 text-slate-300 font-semibold"><Megaphone className="w-4 h-4 text-blue-400" /> New announcement</div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-300">Send to</label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button data-testid="announcement-audience-all" onClick={() => setAudience("all")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${audience === "all" ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                  <Users className="w-4 h-4" /> Everyone
                </button>
                <button data-testid="announcement-audience-individual" onClick={() => setAudience("individual")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${audience === "individual" ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                  <User className="w-4 h-4" /> One person
                </button>
              </div>
            </div>
            {audience === "individual" && (
              <div>
                <label className="text-sm font-medium text-slate-300">Employee</label>
                <select data-testid="announcement-employee-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputCls}>
                  <option value="">Select employee…</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.email ? "" : " (no email)"}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-300">Subject</label>
              <input data-testid="announcement-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Holiday hours this week" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Message</label>
              <textarea data-testid="announcement-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Write your message to the team…" className={`${inputCls} resize-none`} />
            </div>
            <div className="text-xs text-slate-500">
              {audience === "all"
                ? `${withEmail.length} of ${employees.length} employees have an email and will receive this.`
                : "Only sends if this employee has an email on file."}
            </div>
            <button data-testid="announcement-send-button" onClick={send} disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send announcement"}
            </button>
          </div>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden" data-testid="announcement-history">
          <div className="px-5 py-3 border-b border-slate-800 text-sm text-slate-400">Sent history</div>
          <div className="divide-y divide-slate-800/60 max-h-[520px] overflow-y-auto">
            {list.map((a) => (
              <div key={a.id} className="px-5 py-3" data-testid={`announcement-item-${a.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-slate-200">{a.subject}</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 whitespace-nowrap">
                    {a.audience === "all" ? "Everyone" : (a.employee_name || "Individual")}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1 whitespace-pre-wrap line-clamp-3">{a.body}</p>
                <div className="text-[11px] text-slate-500 mt-1.5 font-mono">{fmtDateTime(a.created_at)} · emailed {a.emailed}/{a.recipient_count}</div>
              </div>
            ))}
            {list.length === 0 && <div className="px-5 py-10 text-center text-slate-500 text-sm">No announcements yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
